import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import type { InboxQuestion } from "../../../packages/contracts/src/production";
import type { InboxChange, InboxClosed, InboxFailure, InboxHeld, InboxTeammateChange } from "../../../packages/contracts/src/dashboard";
import { AnswerForm } from "./AnswerForm";
import { LoadingLines } from "./Loading";
import { api } from "./api";
import { UndoChange } from "./MethodImprovement";
import { Button, buttonVariants, cn } from "./components/ui/button";
import { when, at } from "./components/dashboard/format";
import { ProductionApiError, productionApi } from "./production-api";

const linkButton = (variant: "default" | "secondary") => cn(buttonVariants({ variant }));
const Square = ({ kind }: { kind: "waiting" | "attention" | "running" | "quiet" }) =>
  <span className="ico run-squares-mark" aria-hidden="true"><i className={`run-square is-${kind}`} /></span>;

/** The text for a question of a device run. The dashboard cannot read it. */
function SealedQuestion({ runId }: { runId: string }) {
  return <>
    <p>A question is waiting on run {runId}.</p>
    <p className="list-muted">This run keeps its content on devices, so the dashboard cannot show the question. Answer it in your app's ask handler, or run <code>method answer {runId}</code> on a computer that has the app's METHOD_API_KEY.</p>
  </>;
}

/** What waits for you: failed Methods and questions of runs. Also the changes Method made and what closed this week. */
export function InboxPage() {
  const inbox = useQuery({ queryKey: ["inbox"], queryFn: api.inbox, refetchInterval: 15_000 });
  const questions = useQuery({ queryKey: ["production", "questions"], queryFn: productionApi.questions, refetchInterval: 15_000 });
  const data = inbox.data;
  const open = (data?.failures.length ?? 0) + (questions.data?.length ?? 0) + (data?.held.length ?? 0);
  return <div className="p-wrap inbox" aria-label="Inbox">
    <div className="p-bar">
      <Button variant="ghost" size="icon" className="ml-auto" aria-label="Refresh the Inbox" onClick={() => { void inbox.refetch(); void questions.refetch(); }}>
        <ArrowClockwiseIcon size={17} className={inbox.isFetching || questions.isFetching ? "spin" : undefined} />
      </Button>
    </div>
    {inbox.error && <p role="alert">{inbox.error.message}</p>}
    {inbox.isPending ? <LoadingLines /> : data && <>
      {!open && !questions.isPending && <div className="dash-state" role="status"><p>Nothing waits for you.</p></div>}
      {data.failures.length > 0 && <section className="p-section" aria-label="Failed Methods">
        <div className="p-h"><span>Failed Methods</span><small>The last run failed</small></div>
        {data.failures.map(item => <FailureRow key={item.run_id} item={item} />)}
      </section>}
      {data.held.length > 0 && <section className="p-section" aria-label="Judgments that contradict each other">
        <div className="p-h"><span>Judgments that contradict each other</span><small>Nothing is fixed until one is kept</small></div>
        {data.held.map(item => <HeldRow key={`${item.run_id}:${item.step_id ?? ""}`} item={item} />)}
      </section>}
    </>}
    {(questions.error || !!questions.data?.length) && <section className="p-section" aria-label="Questions from runs">
      <div className="p-h"><span>Questions from runs</span></div>
      {questions.error && <p role="alert" className="p-empty">{questions.error.message}</p>}
      {!!questions.data?.length && <ul className="inbox-list inbox-questions">{questions.data.map(question => <QuestionItem key={question.id} question={question} />)}</ul>}
    </section>}
    {!!data?.closed.length && <section className="p-section" aria-label="Closed this week">
      <div className="p-h"><span>Closed this week</span></div>
      {data.closed.map(item => <ClosedLine key={item.failed_run_id} item={item} />)}
    </section>}
    {!!data?.teammates.length && <section className="p-section" aria-label="Teammates changed">
      <div className="p-h"><span>Teammates changed</span><small>This week</small></div>
      {data.teammates.map(item => <TeammateLine key={item.version_id} item={item} />)}
    </section>}
    {!!data?.changes.length && <section className="p-section" aria-label="Changes Method made">
      <div className="p-h"><span>Changes Method made</span><small>This week</small></div>
      {data.changes.map(item => <ChangeLine key={item.id} item={item} />)}
    </section>}
  </div>;
}

function ChangeLine({ item }: { item: InboxChange }) {
  return <div className="closed-line">
    <Square kind="quiet" />
    <span><b>{item.method_name}</b>: {item.cause}{" "}
      <Link to="/methods/$workflowId/proposals/$proposalId" params={{ workflowId: item.method_id, proposalId: item.id }}>
        {item.published_version_number ? `Version ${item.published_version_number}` : "The change"}</Link>{" "}
      {at(item.created_at)}{item.status === "rejected" ? ", then undone" : ""}.</span>
    {item.status !== "rejected" && <UndoChange proposalId={item.id} variant="ghost" />}
  </div>;
}

function HeldRow({ item }: { item: InboxHeld }) {
  return <div className="need ib">
    <Square kind="waiting" />
    <div>
      <b>{item.method_name}</b>{" "}
      <span className="why">· {item.noted_by.join(" and ")} judged {item.step_id ? <>step <i>{item.step_id}</i> of </> : null}the same run in opposite ways {at(item.created_at)}.</span>
    </div>
    <div className="acts"><Link className={linkButton("secondary")} to="/runs/$runId" params={{ runId: item.run_id }}>Choose one</Link></div>
  </div>;
}

function TeammateLine({ item }: { item: InboxTeammateChange }) {
  return <div className="closed-line">
    <Square kind="quiet" />
    <span><b>{item.method_name}</b>: {item.saved_by} saved{" "}
      <Link to="/methods/$workflowId" params={{ workflowId: item.method_id }} search={{ version: item.version_id }}>Version {item.version_number}</Link>{" "}
      {at(item.created_at)}{item.reason ? `: ${item.reason}` : ""}.</span>
  </div>;
}

function FailureRow({ item }: { item: InboxFailure }) {
  return <div className="need ib">
    <Square kind="attention" />
    <div>
      <b>{item.method_name}</b>{" "}
      <span className="why">· The last run {item.stopped_step ? <>failed at <i>{item.stopped_step.name}</i></> : "failed"} {at(item.created_at)}{item.failure ? `: ${item.failure.headline.replace(/\.$/, "")}` : ""}.
        {" "}Started by {item.started_by === "You" ? "you" : item.started_by}.</span>
      {item.attention_in_last_10 > 1 && <div className="sub2">{item.attention_in_last_10} of the last 10 runs failed.</div>}
    </div>
    <div className="acts"><Link className={linkButton("secondary")} to="/runs/$runId" params={{ runId: item.run_id }}>Open the run</Link></div>
  </div>;
}

function ClosedLine({ item }: { item: InboxClosed }) {
  return <div className="closed-line">
    <Square kind="quiet" />
    <span>Closed by itself: <b>{item.method_name}</b> stopped{item.stopped_step ? <> at <i>{item.stopped_step.name}</i></> : null} on version {item.failed_version}.{" "}
      {item.passed_count === 1 ? "1 newer run" : `${item.passed_count} newer runs`} passed{item.stopped_step ? " that step" : ""}.</span>
  </div>;
}

function QuestionItem({ question }: { question: InboxQuestion }) {
  const client = useQueryClient();
  const answer = useMutation({
    mutationFn: (value: Record<string, unknown>) => productionApi.answer(question.run_id, value),
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: ["production"] }), client.invalidateQueries({ queryKey: ["inbox"] })]),
    onError: error => { if (error instanceof ProductionApiError && error.status === 409) void client.invalidateQueries({ queryKey: ["production"] }); },
  });
  return <li className="inbox-item">
    <div>
      <h3>{question.method_name ?? question.method_id}</h3>
      <div className="inbox-meta">
        {question.record_run_id
          ? <Link to="/runs/$runId" params={{ runId: question.record_run_id }}>Open the run</Link>
          : <span>Run {question.run_id}</span>}
        <span>Asked <time dateTime={question.asked_at}>{when(question.asked_at)}</time></span>
        {question.expires_at && <span>Expires <time dateTime={question.expires_at}>{when(question.expires_at)}</time></span>}
      </div>
    </div>
    {question.sealed ? <SealedQuestion runId={question.run_id} /> : <>
      {question.question && <p className="answer-question">{question.question}</p>}
      <AnswerForm form={question.form} pending={answer.isPending} error={answer.error?.message} onSubmit={value => answer.mutate(value)} />
    </>}
  </li>;
}
